/**
 * CourseSrsReview (Việc B — Ôn tập tổng hợp theo khoá).
 *
 * Trang `/courses/:slug/srs`: gom toàn bộ thẻ SRS đến hạn của khoá (thay vì chỉ
 * ôn trong từng bài), cho học viên lật thẻ + tự chấm mức nhớ (Chưa nhớ / Nhớ /
 * Dễ). BE đã sẵn: GET /learning/srs/due/?course=<slug> + POST /srs/review/.
 *
 * Luồng: lật thẻ (click) → chấm → SM-2 xếp lịch lại → thẻ rời danh sách đến hạn
 * (đã ôn xong hôm nay) → đếm còn lại. Hết thẻ → màn hình "đã ôn xong".
 *
 * Lưu ý (model SrsReview): /srs/due chỉ trả thẻ ĐÃ có lịch (học viên từng chấm
 * ≥1 lần trong bài). Thẻ chưa từng chấm = "mới", học viên gặp lần đầu khi học
 * bài — đó là chủ ý thiết kế.
 */

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Card,
  Empty,
  Flex,
  message,
  Progress,
  Result,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { HomeOutlined, ReadOutlined } from "@ant-design/icons";
import { useCourseDetail } from "@/api/catalog";
import { useGradeSrsCard, useSrsDue, type SrsReview } from "@/api/learning";

const { Title, Paragraph, Text } = Typography;

const GRADES = [
  { label: "Chưa nhớ", q: 1 },
  { label: "Nhớ", q: 3 },
  { label: "Dễ", q: 5 },
] as const;

export function CourseSrsReview() {
  const { slug } = useParams<{ slug: string }>();
  const { data: course } = useCourseDetail(slug);
  const { data: due, isLoading } = useSrsDue(slug);
  const grade = useGradeSrsCard();
  const [messageApi, messageHolder] = message.useMessage();

  // Thẻ đã chấm xong trong phiên (ẩn khỏi danh sách đến hạn ngay — optimistic).
  const [reviewed, setReviewed] = useState<Set<number>>(new Set());
  const [flipped, setFlipped] = useState<Record<number, boolean>>({});

  const total = due?.length ?? 0;
  const remaining = useMemo(
    () => (due ?? []).filter((r) => !reviewed.has(r.id)),
    [due, reviewed],
  );
  const doneCount = total - remaining.length;

  const handleGrade = (review: SrsReview, quality: number) => {
    grade.mutate(
      {
        lessonSlug: review.lesson_slug,
        frontJa: review.front_ja,
        backVi: review.back_vi,
        quality,
      },
      {
        onSuccess: (r) => {
          setReviewed((prev) => new Set(prev).add(review.id));
          messageApi.success(`Đã chấm — ôn lại sau ${r.interval_days} ngày`);
        },
        onError: () => messageApi.error("Chấm thẻ thất bại, thử lại sau"),
      },
    );
  };

  const breadcrumb = (
    <Breadcrumb
      style={{ marginBottom: 16 }}
      items={[
        { title: <Link to="/"><HomeOutlined /></Link> },
        { title: <Link to="/courses">Môn học</Link> },
        {
          title: slug ? (
            <Link to={`/my-courses/${slug}`}>{course?.name ?? "Môn học"}</Link>
          ) : (
            "Môn học"
          ),
        },
        { title: "Ôn tập tổng hợp" },
      ]}
    />
  );

  if (isLoading) {
    return (
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        {breadcrumb}
        <div style={{ textAlign: "center", padding: 64 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: "#7a8499" }}>Đang tải thẻ ôn tập...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }} data-testid="srs-review-page">
      {messageHolder}
      {breadcrumb}

      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Tag icon={<ReadOutlined />} color="purple">
              Ôn tập tổng hợp
            </Tag>
            <Title level={3} style={{ margin: "8px 0 0" }}>
              {course?.name ?? "Ôn tập"}
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Gom toàn bộ thẻ đến hạn ôn của khoá. Lật thẻ để xem nghĩa rồi tự chấm
              mức nhớ — hệ thống tự xếp lịch ôn lại (spaced repetition).
            </Paragraph>
          </div>

          {total > 0 && (
            <div data-testid="srs-progress">
              <Flex justify="space-between" align="center">
                <Text strong>
                  Còn {remaining.length} / {total} thẻ
                </Text>
                <Text type="secondary">Đã ôn: {doneCount}</Text>
              </Flex>
              <Progress
                percent={total ? Math.round((doneCount / total) * 100) : 0}
                status={remaining.length === 0 ? "success" : "active"}
              />
            </div>
          )}

          {total === 0 && (
            <Empty
              data-testid="srs-empty"
              description={
                <Space direction="vertical">
                  <Text>Không có thẻ nào đến hạn ôn ngay bây giờ.</Text>
                  <Text type="secondary">
                    Hãy học các bài trong khoá và chấm thẻ — chúng sẽ quay lại đây
                    đúng lịch ôn.
                  </Text>
                </Space>
              }
            >
              {slug && (
                <Link to={`/my-courses/${slug}`}>
                  <Button type="primary">Về môn học</Button>
                </Link>
              )}
            </Empty>
          )}

          {total > 0 && remaining.length === 0 && (
            <Result
              status="success"
              title="Đã ôn xong tất cả thẻ đến hạn!"
              subTitle="Quay lại đúng lịch để ôn tiếp. Việc ôn đều mỗi ngày giúp nhớ lâu hơn."
              extra={
                slug && (
                  <Link to={`/my-courses/${slug}`}>
                    <Button type="primary">Về môn học</Button>
                  </Link>
                )
              }
            />
          )}

          {remaining.length > 0 && (
            <Flex wrap gap={12} data-testid="srs-card-grid">
              {remaining.map((r) => (
                <Card
                  key={r.id}
                  hoverable
                  size="small"
                  onClick={() => setFlipped((f) => ({ ...f, [r.id]: !f[r.id] }))}
                  style={{ width: 200, minHeight: 120, textAlign: "center", cursor: "pointer" }}
                  data-testid="srs-card"
                >
                  <Text strong style={{ fontSize: 18 }}>
                    {flipped[r.id] ? r.back_vi || "—" : r.front_ja}
                  </Text>
                  {flipped[r.id] && (
                    <Flex gap={4} wrap justify="center" style={{ marginTop: 12 }}>
                      {GRADES.map(({ label, q }) => (
                        <Button
                          key={q}
                          size="small"
                          loading={grade.isPending}
                          data-testid={`srs-grade-${q}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGrade(r, q);
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </Flex>
                  )}
                </Card>
              ))}
            </Flex>
          )}
        </Space>
      </Card>
    </div>
  );
}
